import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { collection, getDocs, updateDoc, doc, getDoc } from 'firebase/firestore';
import { setDoc } from 'firebase/firestore';
import '../styles/Admin.css';
import AdminHeader from '../components/AdminHeader';
import AdminSidebar from '../components/AdminSidebar';
import AdminStatsCard from '../components/AdminStatsCard';
import PricingSection from '../components/PricingSection';
import UsersSection from '../components/UsersSection';

const ROOT_EMAIL = 'jusanmagno@gmail.com';

interface UserData {
  id: string;
  isPremium?: boolean;
  premiumType?: string | null;
  premiumActivatedAt?: string | null;
  premiumExpiresAt?: string | null;
  daysActive?: number;
  daysRemaining?: number;
  displayName?: string;
  name?: string;
  email?: string;
}

const Admin: React.FC = () => {
  const { user } = useAuth();
  const [monthlyPrice, setMonthlyPrice] = useState(29.90);
  const [annualPrice, setAnnualPrice] = useState(299.00);
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSection, setSelectedSection] = useState<string>('Dashboard');
  
  // Carrega preços salvos do Firestore
  useEffect(() => {
    if (user?.email !== ROOT_EMAIL) return;
    const loadPrices = async () => {
      try {
        const configDoc = await getDoc(doc(db, 'admin', 'config'));
        if (configDoc.exists()) {
          const data = configDoc.data();
          if (data.monthlyPrice !== undefined) setMonthlyPrice(data.monthlyPrice);
          if (data.annualPrice !== undefined) setAnnualPrice(data.annualPrice);
        }
      } catch (error) {
        console.error('Erro ao carregar preços:', error);
      }
    };
    loadPrices();
  }, [user]);
  
  // Salva preços no Firestore sempre que alterados
  useEffect(() => {
    if (user?.email !== ROOT_EMAIL) return;
    if (monthlyPrice === 29.90 && annualPrice === 299.00) return; // Não salva valores padrão iniciais
    const savePrices = async () => {
      try {
        await setDoc(doc(db, 'admin', 'config'), {
          monthlyPrice,
          annualPrice,
          updatedAt: new Date().toISOString(),
          updatedBy: user?.email
        }, { merge: true });
        console.log('Preços salvos:', { monthlyPrice, annualPrice });
      } catch (error) {
        console.error('Erro ao salvar preços:', error);
      }
    };
    savePrices();
  }, [monthlyPrice, annualPrice, user]);

  useEffect(() => {
    if (user?.email !== ROOT_EMAIL) return;
    const fetchUsers = async () => {
      setLoading(true);
      const snapshot = await getDocs(collection(db, 'users'));
      let list: UserData[] = snapshot.docs.map(docSnap => {
        const data = docSnap.data() || {};
        return {
          id: docSnap.id,
          isPremium: data.isPremium ?? false,
          premiumType: data.premiumType ?? null,
          premiumActivatedAt: data.premiumActivatedAt ?? null,
          premiumExpiresAt: data.premiumExpiresAt ?? null,
          displayName: data.displayName ?? '',
          name: data.name ?? '',
          email: data.email ?? '',
        };
      });
      // Desativa premium automaticamente após 30 dias
      const now = new Date();
      for (const u of list) {
        if (u.isPremium && u.premiumActivatedAt) {
          const activated = new Date(u.premiumActivatedAt);
          const daysActive = Math.floor((now.getTime() - activated.getTime()) / (1000 * 60 * 60 * 24));
          u.daysActive = daysActive;
          
          // Calcula data de expiração (30 dias após ativação)
          const expiresAt = new Date(activated);
          expiresAt.setDate(expiresAt.getDate() + 30);
          u.premiumExpiresAt = expiresAt.toISOString();
          u.daysRemaining = Math.max(0, 30 - daysActive);
          
          if (daysActive > 30) {
            // Desativa premium
            await updateDoc(doc(db, 'users', u.id), { isPremium: false, premiumType: null, premiumActivatedAt: null, premiumExpiresAt: null });
            u.isPremium = false;
            u.premiumType = null;
            u.premiumActivatedAt = null;
            u.premiumExpiresAt = null;
            u.daysActive = 0;
            u.daysRemaining = 0;
          }
        } else {
          u.daysActive = 0;
          u.daysRemaining = 0;
        }
      }
      setUsers(list);
      setLoading(false);
    };
    fetchUsers();
  }, [user]);

  const setPremium = async (uid: string, value: boolean) => {
    const update: any = {
      isPremium: value,
      premiumType: value ? 'manual' : null
    };
    if (value) {
      const now = new Date();
      const expiresAt = new Date(now);
      expiresAt.setDate(expiresAt.getDate() + 30);
      update.premiumActivatedAt = now.toISOString();
      update.premiumExpiresAt = expiresAt.toISOString();
    } else {
      update.premiumActivatedAt = null;
      update.premiumExpiresAt = null;
    }
    await updateDoc(doc(db, 'users', uid), update);
    setUsers(users => users.map(u => u.id === uid ? { ...u, ...update, daysActive: 0, daysRemaining: value ? 30 : 0 } : u));
  };

  // Métricas e valores usados no dashboard
  const totalPremiums = users.filter(u => u.isPremium).length;
  const faturamentoMensalStr = (totalPremiums * monthlyPrice).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });
  const faturamentoAnualStr = (totalPremiums * annualPrice).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });

  // Payments and trends for sparklines and insights
  const [payments, setPayments] = useState<any[]>([]);
  useEffect(() => {
    if (user?.email !== ROOT_EMAIL) return;
    const fetchPayments = async () => {
      try {
        const paymentsSnap = await getDocs(collection(db, 'payments'));
        const list = paymentsSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
        setPayments(list);
      } catch (err) {
        console.error('Erro ao buscar pagamentos:', err);
      }
    };
    fetchPayments();
  }, [user]);

  // Compute monthly revenue buckets (6 months)
  const revenueMonths = (() => {
    const months = 6;
    const now = new Date();
    const buckets: number[] = [];
    for (let i = months - 1; i >= 0; i--) {

      buckets.push(0);
    }
    payments.forEach(p => {
      const created = p.createdAt?.toDate ? p.createdAt.toDate() : (p.createdAt ? new Date(p.createdAt) : null);
      if (!created) return;
      const diffMonths = (now.getFullYear() - created.getFullYear()) * 12 + (now.getMonth() - created.getMonth());
      if (diffMonths < months && diffMonths >= 0) {
        const idx = months - 1 - diffMonths;
        buckets[idx] += Number(p.amount || 0);
      }
    });
    return buckets.map(v => Number(v.toFixed(2)));
  })();

  // Premium user trend (count active premiums at month end)
  const premiumTrend = (() => {
    const months = 6;
    const now = new Date();
    const buckets: number[] = [];
    for (let i = months - 1; i >= 0; i--) {
      const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 0); // last day of that month
      const count = users.filter(u => {
        if (!u.premiumActivatedAt) return false;
        const activated = new Date(u.premiumActivatedAt);
        const expires = u.premiumExpiresAt ? new Date(u.premiumExpiresAt) : null;
        return activated <= end && (!expires || expires >= end);
      }).length;
      buckets.push(count);
    }
    return buckets;
  })();

  if (user?.email !== ROOT_EMAIL) {
    return <div className="admin-page"><h2>Acesso restrito</h2></div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-content">
        <AdminHeader subtitle="Gerencie usuários, preços e pagamentos" />
        <div className="admin-layout">
          <aside>
            <AdminSidebar
              sections={["Dashboard", "Usuários", "Preços", "Pagamentos", "Logs", "Notifications"]}
              selected={selectedSection}
              onSelect={setSelectedSection}
            />
          </aside>
          <main className="admin-main">
          <div className="stats-grid">
            <AdminStatsCard title="Usuários Premium" value={totalPremiums} subtitle={`Manuais: ${users.filter(u => u.isPremium && u.premiumType === 'manual').length} • Sistemáticos: ${users.filter(u => u.isPremium && u.premiumType === 'system').length}`} accent="primary" trend={premiumTrend} />
            <AdminStatsCard title="Faturamento Mensal" value={faturamentoMensalStr} accent="success" trend={revenueMonths} />
            <AdminStatsCard title="Faturamento Anual" value={faturamentoAnualStr} accent="neutral" trend={revenueMonths.map(v => v / 12)} />
            <AdminStatsCard title="Total Usuários" value={users.length} accent="warning" trend={premiumTrend.map(v => v > 0 ? v : 0)} />
          </div>

          <div className="section">
            {selectedSection === 'Dashboard' && (
              <>
                <div className="section-block" style={{display:'flex', gap:12, alignItems:'flex-start', flexWrap:'wrap'}}>
                  <div style={{flex:'1 1 480px'}}>
                    <h3>Visão Geral</h3>
                    <p className="muted">Resumo rápido do estado atual do aplicativo.</p>
                    <div style={{marginTop:12, display:'flex', gap:8}}>
                      <button className="primary">Exportar CSV</button>
                      <button onClick={() => window.location.reload()}>Recarregar</button>
                    </div>
                  </div>
                  <div style={{flex:'1 1 420px'}}>
                    {React.createElement(require('../components/AdminRevenueChart').default, { months: 14, granularity: 'daily' })}
                  </div>
                </div>

                <div style={{display: 'grid', gridTemplateColumns: '1fr 360px', gap: 12, alignItems: 'start'}}>
                  <div>
                    <div className="section-block">
                      <h3>Dashboard de Pagamentos</h3>
                      {React.createElement(require('../components/AdminPaymentsDashboard').default)}
                    </div>
                  </div>
                  <aside style={{minWidth: 320}}>
                    {React.createElement(require('../components/AdminInsights').default, { payments, users })}
                  </aside>
                </div>
              </>
            )}

            {selectedSection === 'Usuários' && (
              <UsersSection users={users} loading={loading} setPremium={setPremium} />
            )}

            {selectedSection === 'Preços' && (
              <PricingSection monthlyPrice={monthlyPrice} annualPrice={annualPrice} setMonthlyPrice={setMonthlyPrice} setAnnualPrice={setAnnualPrice} />
            )}

            {selectedSection === 'Pagamentos' && (
              <div className="section-block">
                <h3>Pagamentos</h3>
                {React.createElement(require('../components/AdminPaymentsDashboard').default)}
              </div>
            )}

            {selectedSection === 'Logs' && (
              <div>
                {React.createElement(require('../components/LogsSection').default)}
              </div>
            )}

            {selectedSection === 'Notifications' && (
              <div>
                {React.createElement(require('../components/NotificationsReport').default)}
              </div>
            )}
          </div>
        </main>
        </div>
      </div>
    </div>
  );
};

export default Admin;
